import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import LoadingSpinner from '@ui/loading-spinner/loading-spinner';
import CustomCarousel from '@ui/custom-carousel/custom-carousel';
import type { TCustomCarouselScrollTo } from '@ui/custom-carousel/custom-carousel.types';
import { useLoadCampaignTemplates } from '../../create/hooks/use-load-campaign-templates';
import { campaignTemplatesMap } from '../../templates';
import TemplateStripCard from './template-strip-card';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectCampaignTemplate } from '@/store/slices/campaign-editor';
import { selectCampaignEditorCurrentTemplateId } from '@/store/slices/campaign-editor/selectors';

interface TemplateStripProps {
  timezone?: string;
  onClose: () => void;
  hidden?: boolean;
}

export default function TemplateStrip({ timezone, onClose, hidden = false }: TemplateStripProps) {
  const dispatch = useAppDispatch();
  const currentTemplateId = useAppSelector(selectCampaignEditorCurrentTemplateId);
  const onSelect = useCallback((templateUuid: string) => dispatch(selectCampaignTemplate(templateUuid)), [dispatch]);
  const { templates, loading } = useLoadCampaignTemplates();
  const mappedTemplates = useMemo(
    () => templates.filter((template) => campaignTemplatesMap.has(template.uuid)),
    [templates]
  );
  const carouselRef = useRef<HTMLDivElement>(null);
  const [scrollTo, setScrollTo] = useState<TCustomCarouselScrollTo | undefined>(undefined);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const idx = mappedTemplates.findIndex((template) => template.uuid === currentTemplateId);
    if (idx >= 0) setActiveIndex(idx);
  }, [currentTemplateId, mappedTemplates]);

  useEffect(() => {
    const node = carouselRef.current;
    if (!node) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      const currentIndex = Number((e.target as HTMLElement).closest('[data-index]')?.getAttribute('data-index'));
      if (Number.isNaN(currentIndex)) return;

      const nextIndex = e.key === 'ArrowLeft' ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= mappedTemplates.length) return;

      e.preventDefault();
      setScrollTo({ index: nextIndex, align: 'center' });
      node.querySelector<HTMLElement>(`[data-index="${nextIndex}"] button`)?.focus();
    };

    node.addEventListener('keydown', handleKeyDown);
    return () => node.removeEventListener('keydown', handleKeyDown);
  }, [mappedTemplates]);

  if (loading) {
    return (
      <Container className="flex ai-center jc-center" height="270px">
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container className="position-relative">
      <Button
        clear
        position="absolute"
        backgroundColor="var(--gray-700)"
        hoverBackgroundColor="var(--gray-600)"
        border="2px solid var(--BG, #1a1a1f)"
        borderRadius="50%"
        width="32px"
        height="32px"
        padding="10px"
        className="flex ai-center jc-center top-16 left-8 z-2 flex-shrink-0"
        onClick={onClose}
        aria-label="Close template list"
      >
        <Icon name={ICONS.CLOSE} size={12} color="var(--global-ico-text-1)" />
      </Button>

      <Container ref={carouselRef} height="320px" padding="20px">
        {!hidden && (
          <CustomCarousel
            gap="12px"
            itemContainerDimensions={{ width: '180px', height: '100%' }}
            list={mappedTemplates.map((template) => ({ ...template, id: template.uuid }))}
            scrollTo={scrollTo}
            renderItem={(template, idx) => (
              <TemplateStripCard
                template={{ ...template, component: campaignTemplatesMap.get(template.uuid)! }}
                isSelected={template.uuid === currentTemplateId}
                timezone={timezone}
                tabIndex={idx === activeIndex ? 0 : -1}
                onFocus={() => setActiveIndex(idx)}
                onSelect={onSelect}
              />
            )}
          />
        )}
      </Container>
    </Container>
  );
}
